import { IconButton } from "@/components/ui/icon-button";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import type { ClientReviewData } from "@/types/review";
import { useEffect, useState } from "react";
import { Typography } from "@/components/shared/typography";
import type { CarouselApi } from "@/components/ui/carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import type { ServiceName } from "@/types/service";
import { Image, ScrollArea, View } from "@/components/ui";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { ReviewCard } from "@/components/shared/review-card/review-card";
import { CustomerReviewList } from "./customer-review-list";
import { ServiceDetailNavigation } from "../service-detail-navigation";

type CustomerReviewModalProps = {
  open: boolean;
  onClose: () => void;
  serviceName: ServiceName;
};

export const CustomerReviewModal = ({
  open,
  onClose,
  serviceName,
}: CustomerReviewModalProps) => {
  const { isDesktop } = useWindowDimensions();
  const [selectedReview, setSelectedReview] = useState<
    (ClientReviewData & { imageIndex: number }) | null
  >(null);

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    if (api && selectedReview?.imageIndex) {
      api.scrollTo(selectedReview.imageIndex, true);
    }
  }, [api, selectedReview?.imageIndex]);

  if (selectedReview) {
    return (
      <BottomDrawerModal
        open={!!selectedReview}
        onOpenChange={() => setSelectedReview(null)}
        className="relative p-0"
      >
        {isDesktop && (
          <View className="flex flex-row items-center justify-between p-4">
            <Typography variant="body-md">Customer review</Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              size="md"
              iconName="x"
              onClick={() => setSelectedReview(null)}
            />
          </View>
        )}
        <View>
          {selectedReview.images.length > 0 && (
            <Carousel setApi={setApi}>
              <View className="flex flex-row items-center justify-between p-4 pt-0">
                <IconButton
                  variant={isDesktop ? "secondary" : "ghost"}
                  color="foreground"
                  iconName="arrowLeft"
                  rounded="full"
                  className="p-0 md:px-3 md:py-2"
                  onClick={() => setSelectedReview(null)}
                />
                <Typography variant="body-sm" color="foreground-intermediate">
                  {current}/{count}
                </Typography>
              </View>
              <CarouselContent className="ml-0 h-[400px]">
                {selectedReview.images.map((image) => (
                  <CarouselItem key={image} className="pl-0">
                    <Image
                      src={image}
                      className="h-[400px] w-full rounded-md object-contain"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <IconButton
                variant="ghost"
                color="foreground"
                rounded="full"
                size="sm"
                iconName="caretLeft"
                className="absolute left-2 top-1/2 z-50"
                disabled={!api?.canScrollPrev()}
                onClick={() => api?.scrollPrev()}
              />
              <IconButton
                variant="ghost"
                color="foreground"
                rounded="full"
                size="sm"
                iconName="caretRight"
                className="absolute right-2 top-1/2 z-50"
                disabled={!api?.canScrollNext()}
                onClick={() => api?.scrollNext()}
              />
            </Carousel>
          )}
          <ReviewCard
            className="border-0"
            clientName={selectedReview.clientName}
            ltv={selectedReview.ltv}
            rating={selectedReview.rating}
            date={selectedReview.date}
            service={selectedReview.service}
            description={selectedReview.description}
          />
        </View>
        <View className="md:p-4">
          <ServiceDetailNavigation name={serviceName} showPricingSection />
        </View>
      </BottomDrawerModal>
    );
  }

  return (
    <BottomDrawerModal
      open={open}
      onOpenChange={onClose}
      className="relative md:p-0"
    >
      {isDesktop && (
        <View className="flex flex-row items-center justify-between p-4">
          <Typography variant="body-md">Customer review</Typography>
          <IconButton
            variant="ghost"
            color="foreground"
            size="md"
            iconName="x"
            onClick={onClose}
          />
        </View>
      )}
      <ScrollArea className="md:h-[calc(70vh-100px)] md:px-4">
        <CustomerReviewList
          serviceName={serviceName}
          handlePreviewReview={setSelectedReview}
        />
      </ScrollArea>
      <View className="md:p-4">
        <ServiceDetailNavigation name={serviceName} showPricingSection />
      </View>
    </BottomDrawerModal>
  );
};
